import type { ReactNode } from 'react'

import type { Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Label } from '@/components/ui'
import { FormField, FormItem } from '@/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { CardEtapaContent, CardEtapaItem } from '@/features/proposta/shared/components/ui/card-etapa'

type TImobiliariaCorretorStepBaseProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  completed: boolean
  onSelecionar: (field: { onChange: (value: string) => void }) => (value: string) => void
  children: ReactNode
}

export const ImobiliariaCorretorStepBase = <TFieldValues extends FieldValues>({
  control,
  name,
  completed,
  onSelecionar,
  children,
}: TImobiliariaCorretorStepBaseProps<TFieldValues>) => {
  const { t } = useTranslation('proposta')

  const labelSim = t('detalhes_proposta.imobiliaria.inputs.radio.sim')
  const labelNao = t('detalhes_proposta.imobiliaria.inputs.radio.nao')

  return (
    <CardEtapaItem
      titulo={t('detalhes_proposta.imobiliaria.titulo')}
      descricao={t('detalhes_proposta.imobiliaria.descricao')}
      completed={completed}
    >
      <CardEtapaContent>
        <span className="text-sm font-medium text-neutral-800">
          {t('detalhes_proposta.imobiliaria.inputs.radio.label')}
        </span>
        <FormField
          control={control}
          name={name}
          render={({ field }) => (
            <FormItem>
              <RadioGroup className="flex flex-col gap-6" value={field.value} onValueChange={onSelecionar(field)}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="Sim" value="Sim" />
                  <Label htmlFor="Sim" aria-labelledby="Sim" className="font-nunito text-base font-normal">
                    {labelSim}
                  </Label>
                </div>
                <Render.If condition={field.value === 'Sim'}>{() => children}</Render.If>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="Nao" value="Não" />
                  <Label htmlFor="Nao" aria-labelledby="Nao" className="font-nunito text-base font-normal">
                    {labelNao}
                  </Label>
                </div>
              </RadioGroup>
            </FormItem>
          )}
        />
      </CardEtapaContent>
    </CardEtapaItem>
  )
}
